import * as React from 'react';
import { useLoaderData, useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Alert, Box, Typography } from '@mui/material';
import { EntityDetailCard, EntityDetailCardSection } from '../EntityDetailCard';
import {
  formatTime,
  jobIconStatusMap,
  jobStatusColorMap,
  stringToMoment,
} from '../utils';
import { formatDistance, formatDistanceStrict, parseJSON } from 'date-fns';
import { useGlobalSettings } from '../../../app/GlobalSettingProvider';
import FlowResourceDisplay from '../FlowResourceDisplay';
import { TabLayout, Tab } from '../TabLayout';
import { useAccount } from '../../../auth';
import { useEntities, useEntity } from '../../../data/flowEntities';
import CronTableCellDisplay from '../CronTableCellDisplay';
import SyncObjectsTable from './SyncObjectsTable';
import SyncChildJobsTable from './syncChildJobsTable';
import { PageTitle } from '../../PageTitle';
import { theme } from '../../../utils/theme';
import CopyToClipboard from '../../CopyToClipboard';
import { TimeZoneSelector } from '../../header/TimeZoneSelector';
import { Flow } from '../../../types/flow.types';
import JobLogsTable from './JobLogsTable';
import { buildBasicQuery } from '../../logs/buildBasicQuery';

export const SyncEntityContainer: React.FC = () => {
  const { syncId, entityId } = useParams();
  const { currentAccount } = useAccount();
  const { settings } = useGlobalSettings();
  const formatTimeTZ = formatTime(settings.timeZone.current);

  const flowQuery = useEntity(currentAccount?.id, 'flow', entityId, {
    include: ['source', 'destinations'],
    refetchInterval: 0,
  });

  // @ts-expect-error flow is not typed
  const flow = flowQuery?.data as Flow;

  const [shouldRefetch, setShouldRefetch] = React.useState(false);

  const refetchInterval = shouldRefetch ? 2000 : 0;

  const syncStatsQuery = useEntity(
    currentAccount?.id,
    'job',
    syncId + '/sync-stats',
    { refetchInterval },
  );

  React.useEffect(() => {
    if (syncStatsQuery.status) {
      const syncHasFinished = ['success', 'failed'].includes(
        // @ts-expect-error syncStatsQuery.data is not typed
        syncStatsQuery?.data?.sync_job?.status,
      );

      if (!syncHasFinished) {
        setShouldRefetch(true);
      } else {
        setShouldRefetch(false);
      }
    }
  }, [syncStatsQuery]);

  const syncStats = syncStatsQuery?.data;

  // @ts-expect-error syncStats is not typed
  const sync = syncStats?.sync_job as Job;

  // @ts-expect-error syncStats is not typed
  const stats = syncStats?.child_stats || [];

  // Fetch child jobs to get all job IDs for log queries
  const childJobsQuery = useEntities(
    currentAccount?.id,
    `jobs/${syncId}/child-jobs`,
    { useExplicitEntityType: true, size: 100 },
    { enabled: Boolean(syncId && currentAccount?.id) },
  );
  const logsJobFilter = React.useMemo(() => {
    const children = childJobsQuery?.data?.data || childJobsQuery?.data || [];
    const ids = (children as any[]).map((j: any) => j.id).filter(Boolean);
    if (ids.length > 0) {
      return { job_id: ids.join(',') };
    }
    // Fallback to flow_job_id until child jobs load
    return { flow_job_id: syncId };
  }, [childJobsQuery?.data, syncId]);

  // Job scope filter — always prepended so queries are scoped to this sync
  const jobFilterPrefix = React.useMemo(() => {
    if (logsJobFilter.job_id) {
      const ids = logsJobFilter.job_id.split(',');
      const quoted = ids.map((id: string) => `"${id}"`).join(', ');
      return `filter job_id in [${quoted}]`;
    }
    if (logsJobFilter.flow_job_id) {
      return `filter flow_job_id = "${logsJobFilter.flow_job_id}"`;
    }
    return '';
  }, [logsJobFilter]);

  const defaultQuery = 'sort @timestamp desc';

  const [queryMode, setQueryMode] = React.useState<'basic' | 'advanced'>(
    'basic',
  );
  const [basicSearchText, setBasicSearchText] = React.useState('');
  const [userQuery, setUserQuery] = React.useState<string>('');

  const effectiveQuery =
    queryMode === 'basic'
      ? [jobFilterPrefix, buildBasicQuery(basicSearchText)]
          .filter(Boolean)
          .join(' | ')
      : [jobFilterPrefix, userQuery || defaultQuery]
          .filter(Boolean)
          .join(' | ');

  // Fetch schedules for this flow
  const schedulesQuery = useEntities(
    currentAccount?.id,
    'schedule',
    {
      filter: {
        entity_type: 'flow',
        entity_id: flow?.id,
      },
    },
    { enabled: Boolean(flow?.id && currentAccount?.id) },
  );
  const schedules = schedulesQuery?.data?.data || [];
  // Find the schedule for this sync (by triggered_by_id)
  const syncSchedule = schedules.find(
    (s: { id: string }) => s.id === sync?.triggered_by_id,
  );

  // if sync status success make it healthy if its failed make it unhealthy
  // otherwise make it unknown
  const cardStatus =
    sync?.status === 'success'
      ? 'healthy'
      : sync?.status === 'failed'
        ? 'unhealthy'
        : sync?.status === 'running'
          ? 'running'
          : 'unknown';

  const [logsTimeRange, setLogsTimeRange] = React.useState<{
    start: string;
    end: string;
  } | null>();

  // Set logsTimeRange based on sync run_at and ended_at
  React.useEffect(() => {
    if (sync?.run_at && sync?.ended_at) {
      setLogsTimeRange({
        start: sync.run_at,
        end: sync.ended_at,
      });
    }
  }, [sync?.run_at, sync?.ended_at, settings.timeZone.current]);

  return (
    <>
      <PageTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: theme.spacing(8),
        }}
      >
        <TimeZoneSelector />
      </PageTitle>

      <ErrorBoundary
        fallback={
          <Alert
            sx={{ maxWidth: '1200px', margin: '24px auto' }}
            severity="error"
          >
            Error loading sync details.
          </Alert>
        }
      >
        <EntityDetailCard status={cardStatus}>
          <EntityDetailCardSection
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            <span style={{ marginLeft: -16, marginRight: 4, marginTop: 3 }}>
              {sync?.status &&
                jobIconStatusMap[sync.status as keyof typeof jobIconStatusMap]}
            </span>
            <span>
              <Typography
                variant="body1"
                color={
                  jobStatusColorMap[
                    sync?.status as keyof typeof jobStatusColorMap
                  ]
                }
                fontWeight={900}
              >
                {sync?.status}
              </Typography>
              {sync?.id && (
                <CopyToClipboard
                  sx={{ marginTop: 2 }}
                  displayText={
                    sync?.id?.split('-')[sync?.id?.split('-').length - 1]
                  }
                >
                  {sync?.id}
                </CopyToClipboard>
              )}
            </span>
          </EntityDetailCardSection>
          <EntityDetailCardSection>
            <Typography variant="overline" fontWeight={900}>
              SOURCE
            </Typography>
            <FlowResourceDisplay flow={flow} direction="source" />
            <hr />
            <Typography variant="overline" fontWeight={900}>
              DESTINATION(S)
            </Typography>
            <FlowResourceDisplay flow={flow} direction="destination" />
          </EntityDetailCardSection>
          {syncSchedule?.schedule && (
            <EntityDetailCardSection>
              <>
                <Typography variant="overline" color="black" fontWeight={900}>
                  Schedule ( {syncSchedule.schedule} )
                </Typography>

                <Typography variant="body2" color="black">
                  <CronTableCellDisplay
                    params={{ value: { schedule: syncSchedule.schedule } }}
                  />
                </Typography>
              </>
            </EntityDetailCardSection>
          )}

          <EntityDetailCardSection>
            <Typography variant="overline" fontWeight={900}>
              Queued time
            </Typography>
            <Typography variant="body2" color="initial">
              {sync?.run_at ? formatTimeTZ(parseJSON(sync.run_at)) : 'N/A'}
            </Typography>
          </EntityDetailCardSection>

          <EntityDetailCardSection>
            <Typography variant="overline" fontWeight={900}>
              Started
            </Typography>
            <Typography variant="body2" color="black">
              {sync?.started_at && formatTimeTZ(parseJSON(sync?.started_at))}
            </Typography>
          </EntityDetailCardSection>

          <EntityDetailCardSection>
            <Typography variant="overline" fontWeight={900}>
              Ended
            </Typography>
            <Typography variant="body2" color="black">
              {sync?.ended_at && formatTimeTZ(parseJSON(sync?.ended_at))}
            </Typography>
          </EntityDetailCardSection>

          <EntityDetailCardSection>
            <Typography variant="overline" fontWeight={900}>
              Run time
            </Typography>
            <Typography variant="body2" color="initial">
              {sync?.ended_at &&
                sync?.started_at &&
                formatDistanceStrict(
                  parseJSON(sync?.ended_at),
                  parseJSON(sync?.started_at),
                )}
            </Typography>
          </EntityDetailCardSection>
        </EntityDetailCard>

        <TabLayout ariaLabel="flow-detail">
          <Tab name="objects" title="Objects">
            <SyncObjectsTable stats={stats || []} />
          </Tab>
          <Tab name="child-jobs" title="Child Jobs">
            <SyncChildJobsTable
              sync={sync}
              currentAccount={currentAccount}
              syncId={syncId || ''}
              refetchInterval={refetchInterval}
            />
          </Tab>
          <Tab name="logs" title="Logs">
            <JobLogsTable
              query={effectiveQuery}
              displayQuery={
                queryMode === 'basic'
                  ? basicSearchText
                  : userQuery || defaultQuery
              }
              defaultQuery={defaultQuery}
              onQueryChange={
                queryMode === 'basic' ? setBasicSearchText : setUserQuery
              }
              queryMode={queryMode}
              onQueryModeChange={setQueryMode}
              startTime={logsTimeRange?.start}
              endTime={logsTimeRange?.end}
              onTimeChange={({ start, end }) =>
                setLogsTimeRange({ start, end })
              }
              currentAccount={currentAccount}
              timezone={settings.timeZone.current}
              sync={sync}
              jobFilterPrefix={jobFilterPrefix}
            />
          </Tab>
        </TabLayout>
      </ErrorBoundary>
    </>
  );
};
